import { WhatsAppIcon } from "@/src/view/contact/ContactView"; // مطمئن شوید این ایمپورت صحیح است
import { Copyright, Instagram, Phone, Send } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

// FooterColumn: افزایش padding عمودی لینک‌ها برای بهبود Touch Target
const FooterColumn = ({ title, links }: { title: string; links: { href: string; label: string }[] }) => (
  <div className="flex flex-col items-center text-center sm:text-right sm:items-start gap-1">
    <h3 className="text-[#223843] dark:text-slate-200 font-bold mb-2 text-base">
      {title}
    </h3>
    {/* افزایش space-y برای فاصله بیشتر بین آیتم ها */}
    <ul className="space-y-1">
      {links.map((link) => (
        <li key={link.href}>
          <Link
            href={link.href}
            className="text-gray-600 dark:text-slate-300 hover:text-[#223843] dark:hover:text-slate-100 transition inline-block py-2 px-1" // افزایش padding عمودی py-2
          >
            {link.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

// FooterIconLink: افزایش اندازه آیکون و padding دور لینک برای بهبود Touch Target
const FooterIconLink = ({ href, Icon, label }: { href: string; Icon: React.ElementType; label: string }) => (
  <Link
    href={href}
    aria-label={label}
    target={(href.startsWith("http") || href.startsWith("tel:") || href.startsWith("mailto:")) ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    className="text-gray-500 dark:text-gray-400 hover:text-[#108804] dark:hover:text-[#3bc918] transition group rounded-md p-2.5" // افزایش padding کلی p-2.5
  >
    {/* افزایش اندازه آیکون */}
    <Icon className="w-6 h-6 text-[#223843] dark:text-slate-300 group-hover:text-[#108804] dark:group-hover:text-[#3bc918] transition" />
  </Link>
);


const FooterView = ({ show = true }: { show?: boolean }) => {
  if (!show) return null;

  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { href: "https://www.instagram.com/kific.o?igsh=Mm9xNjBuMjQ4dmQw", Icon: Instagram, label: "اینستاگرام هیرونیا" },
    { href: "https://t.me/hirooniaomde", Icon: Send, label: "تلگرام هیرونیا" },
    { href: "tel:09338885007", Icon: Phone, label: "تماس با هیرونیا" },
    { href: "https://wa.me/989931729786", Icon: WhatsAppIcon, label: "واتساپ هیرونیا" }, // مطمئن شوید WhatsAppIcon به درستی تعریف شده
  ];

  return (
    <footer className="bg-white dark:bg-slate-800 border-t-4 border-[#223843] pt-8 pb-6 text-xs sm:text-sm">
      <div className="container mx-auto max-w-7xl px-4">

        <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mb-8">
          <div className="flex flex-col items-center sm:items-start gap-3">
            <Link href="/" aria-label="صفحه اصلی هیرونیا">
              <Image
                src="/hiroonia.png"
                alt="لوگوی متنی هیرونیا"
                width={100}
                height={50}
              />
            </Link>
            {/* تنظیم gap برای آیکون‌های اجتماعی با توجه به padding جدید */}
            <div className="flex gap-2">
              {socialLinks.map((link) => (
                <FooterIconLink
                  key={link.label}
                  href={link.href}
                  Icon={link.Icon}
                  label={link.label}
                />
              ))}
            </div>
          </div>
          <div className="mt-4 sm:mt-0">
            <a
              href="https://trustseal.enamad.ir/?id=275118&amp;Code=lxkhvhgrxbRv9PBTINup"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="نماد اعتماد الکترونیکی فروشگاه هیرونیا"
              className="inline-block p-1" // کمی پدینگ برای افزایش touch target اینماد
            >
              <Image
                src="/enamad.png"
                alt="نماد اعتماد الکترونیکی (eNAMAD)"
                width={70}
                height={90}
              />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-8 mb-8 text-center sm:text-right">
          <FooterColumn title="اطلاعات" links={[
            { href: "/about", label: "درباره ما" },
            { href: "/contact", label: "تماس با ما" },
            { href: "/rules", label: "قوانین و مقررات" },
            { href: "/faq", label: "سوالات متداول" },
            { href: "/track-order", label: "پیگیری سفارش" },
          ]} />

          <FooterColumn title="دسته‌بندی‌ها" links={[
            { href: "/categories/airfryer", label: "سرخ کن" },
            { href: "/categories/Iron", label: "اتو" },
            { href: "/categories/hand-blender", label: "گوشت کوب" },
            { href: "/categories/espressomaker", label: "اسپرسو ساز" },
            { href: "/categories/Cordless-vacuum-cleaner", label: "جاروبرقی" },
          ]} />

          <FooterColumn title="صفحات دیگر" links={[
            { href: "/mag", label: "مجله هیرونیا" },
            { href: "/brand", label: "برندها" },
          ]} />

          <FooterColumn title="همکاری با ما" links={[
            { href: "/collaboration", label: "فرصت‌های همکاری" },
            { href: "/wholesale", label: "فروش عمده" },
          ]} />
        </div>
      </div>

      <div className="mt-8 pt-4 border-t border-gray-200 dark:border-slate-700 flex justify-center items-center text-gray-500 dark:text-slate-400 text-[10px] sm:text-xs">
        <p className="ml-1">
          کلیه حقوق این وب‌سایت متعلق به فروشگاه هیرونیا است. Copyright{" "}
          {currentYear} © Hiroonia
        </p>
        <Copyright className="w-4 h-4" />
      </div>
    </footer>
  );
};

export default FooterView;